'use client';

import type { FC } from 'react';
import { cn } from '../../utils/cn';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useCurrentTime } from '../../hooks/useCurrentTime';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import { Credits } from '../Credits';
import { RiLockLine } from '../icons';
import translations from './caseBattle.translations';
import { modeOf, type Seat } from './battleModel';

export const useCaseBattleT = () => useLocalTranslation(translations);

export const useModeLabel = () => {
  const { t } = useCaseBattleT();
  return (mode: string) => {
    const m = modeOf(mode);
    return m === 'crazy' ? t('modeCrazy') : m === 'sharing' ? t('modeSharing') : m === 'last_case' ? t('modeLastCase') : t('modeClassic');
  };
};

export const useModeRule = () => {
  const { t } = useCaseBattleT();
  return (mode: string) => {
    const m = modeOf(mode);
    return m === 'crazy' ? t('modeCrazyRule') : m === 'sharing' ? t('modeSharingRule') : m === 'last_case' ? t('modeLastCaseRule') : t('modeClassicRule');
  };
};

export const useFormatLabel = () => {
  const { t } = useCaseBattleT();
  return (isTeam: boolean, maxPlayers: number) =>
    isTeam ? t('formatTeam') : maxPlayers === 3 ? t('format3') : maxPlayers === 4 ? t('format4') : t('format2');
};

export const ModeBadge: FC<{ mode: string; className?: string }> = ({ mode, className }) => {
  const modeLabel = useModeLabel();
  const m = modeOf(mode);
  return (
    <span
      className={cn(
        'inline-flex items-center h-6 px-2 rounded-full text-[12px] leading-none font-bold whitespace-nowrap',
        m === 'crazy' && 'bg-accent-purple/15 text-accent-purple',
        m === 'sharing' && 'bg-accent-green/15 text-accent-green',
        m === 'last_case' && 'bg-accent-orange/15 text-accent-orange',
        m === 'classic' && 'bg-white/[0.06] text-label-secondary',
        className,
      )}
    >
      {modeLabel(mode)}
    </span>
  );
};

export const FormatBadge: FC<{ isTeam: boolean; maxPlayers: number; isPrivate?: boolean; className?: string }> = ({
  isTeam,
  maxPlayers,
  isPrivate = false,
  className,
}) => {
  const formatLabel = useFormatLabel();
  const { t } = useCaseBattleT();
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[13px] font-semibold text-label-secondary whitespace-nowrap', className)}>
      {formatLabel(isTeam, maxPlayers)}
      {isPrivate && (
        <span className="inline-flex items-center gap-0.5 text-label-tertiary" title={t('privateTable')}>
          <RiLockLine size={14} />
        </span>
      )}
    </span>
  );
};

export const SidePill: FC<{ side: 0 | 1; className?: string }> = ({ side, className }) => {
  const { t } = useCaseBattleT();
  return (
    <span
      className={cn(
        'inline-flex items-center h-5 px-1.5 rounded text-[11px] font-bold leading-none',
        side === 0 ? 'bg-accent-blue/20 text-accent-blue' : 'bg-accent-orange/20 text-accent-orange',
        className,
      )}
    >
      {side === 0 ? t('sideA') : t('sideB')}
    </span>
  );
};

export const SeatAvatar: FC<{ seat: Seat; size?: 's' | 'm' | 'l' | 'xl'; className?: string }> = ({ seat, size = 'm', className }) => (
  <UserAvatar userId={seat.userId} firstName={seat.firstName ?? null} level={seat.level ?? 1} size={size} className={className} />
);

export const useCountdown = (deadline: string | null | undefined): number => {
  const now = useCurrentTime();
  if (!deadline) return 0;
  return Math.max(0, Math.ceil((new Date(deadline).getTime() - now) / 1000));
};

interface CreditsTextProps {
  template: string;
  amount: number;
  placeholder?: string;
  iconSize?: number;
  className?: string;
  creditsClassName?: string;
  compact?: boolean;
}

export const CreditsText: FC<CreditsTextProps> = ({ template, amount, placeholder = 'amount', iconSize = 12, className, creditsClassName, compact = false }) => {
  const [before, after] = template.split(`{{${placeholder}}}`);
  return (
    <span className={className}>
      {before?.trim() && <>{before.trim()} </>}
      <Credits amount={amount} iconSize={iconSize} compact={compact} className={cn('inline-flex align-middle', creditsClassName)} />
      {after?.trim() && <> {after.trim()}</>}
    </span>
  );
};

export const formatMmSs = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};
